import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from '../messages';

import { Box, Typography, Grid2 as Grid, Card, CardContent, IconButton, Modal, TextField, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getStyles } from './styles';
import all_connectors from "./data.json";
import { ReactSVG } from 'react-svg';
import { IconSvg } from '../../../../utils/globalIcons';
import { ArrowForward, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoadBar from '../../../../utils/LoadBar';
import moment from 'moment';
import { fetchSubscriptionId, getUserName } from '../../../../utils/GetAccountDetails';
import { tostAlert } from '../../../../utils/AlertToast';
import { getMainStyles } from '../../../../styles/styles';
import { useAppContext } from '../../../../context/AppContext';
import AnalyticsDataSourcesApi from '../../../../Api/Services/AppflyteBackend/AnalyticsDataSources';
import { setAnalyticsDataSourcesAdded } from '../../../../Redux/slice/newDataSlice';
import { IS_PROD_INSTANCE, IS_SFS_INSTANCE, IS_COMMUNITY_MODE, IS_AWS_MODE } from '../../../../utils';

function AnalyticsConnectors({ setSelectedMenuItem }) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const mainStyles = getMainStyles(theme);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();
    const { selectedWorkspace, selectedProject, handleAnalyticsSteps } = useAppContext();

    const [loading, setLoading] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const modalDetails = { modalstatus: false, connection_type: '' };
    const [modalData, setModalData] = useState(modalDetails);
    const [connectionName, setConnectionName] = useState('');
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        const errors = { ...formErrors };
        if (formErrors?.connectionName && connectionName.trim()) { delete errors?.connectionName }
        if (JSON.stringify(errors) !== JSON.stringify(formErrors)) { setFormErrors(errors) }
    }, [connectionName, modalData, formErrors]);

    const typeConfig = {
        google: { icon: IconSvg.driveIcon, label: "Google Drive" },
        azure: { icon: IconSvg.onedriveIcon, label: "Microsoft OneDrive" },
        gmail: { icon: IconSvg.mailIcon, label: "Gmail" },
        outlook: { icon: IconSvg.mailIcon, label: "Outlook" }
    };
    const currentType = typeConfig[modalData.connection_type];

    const handleConnectorSelection = async (type, label) => {
        const validTypes = ["google", "azure", "gmail", "outlook"];
        if (validTypes.includes(type)) {
            setModalData({ ...modalData, modalstatus: true, connection_type: type });
        } else {
            navigate(`/sources/analytics-tool/integrations/add`, {
                state: {
                    connection_type: type,
                    connection_label: label,
                    action_type: 'post'
                }
            })
        }
    }

    const waitForPopupMessage = (accept) => {
        return new Promise((resolve) => {
            const handler = (event) => {
                if (accept(event.data)) {
                    window.removeEventListener('message', handler);
                    resolve(event.data);
                }
            };
            window.addEventListener('message', handler);
        });
    };

    const handleConnectionIntegration = async () => {
        setAuthLoading(true);
        try {
            const errors = {};
            if (!connectionName.trim()) {
                errors.connectionName = 'This field is required';
            }

            if (Object.keys(errors).length > 0) {
                setFormErrors(errors);
                setAuthLoading(false);
                return;
            }

            try {
                setModalData({ ...modalData, modalstatus: false });
                const subscriptionId = await fetchSubscriptionId();
                const appName = window.__ENV__.APP_NAME;
                const origin_url = window.__ENV__.AMEYA_WEB_URL;
                const auth_server_url = window.__ENV__.APPFLYTE_AUTHENTICATION_URL;

                const authMap = { gmail: "google", outlook: "azure" };
                const connection_auth_type = authMap[modalData.connection_type] || modalData.connection_type;

                const serviceTypeMap = { google: "gdrive", azure: "onedrive", gmail: "gmail", outlook: "outlook" };
                const service_type = serviceTypeMap[modalData.connection_type];

                const response = await fetch(`${auth_server_url}/ameya_service/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        provider: connection_auth_type,
                        subscription_id: subscriptionId,
                        app_name: appName,
                        origin_url: origin_url,
                        service_type: service_type
                    }),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                const { auth_url } = data;

                if (!auth_url) {
                    throw new Error('No auth URL received from server');
                }

                const popup = window.open(auth_url, 'oauth_popup', 'width=600,height=600');
                if (!popup) {
                    throw new Error('Popup was blocked. Please allow popups for this site.');
                }

                popup.focus();

                const popupMessagePromise = waitForPopupMessage((d) => d?.source === 'oauth-popup' && typeof d.user_id === 'string');
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('OAuth timeout')), 60000);
                });

                const popupClosedPromise = new Promise((_, reject) => {
                    const checkClosed = setInterval(() => {
                        if (popup.closed) {
                            clearInterval(checkClosed);
                            reject(new Error('OAuth popup was closed'));
                        }
                    }, 1000);
                });

                const { user_id, user_mail, refresh_token } = await Promise.race([
                    popupMessagePromise,
                    timeoutPromise,
                    popupClosedPromise
                ]);

                if (!popup.closed) {
                    popup.close();
                }

                await handleConnectionAuth(modalData.connection_type, user_id, user_mail, refresh_token);

            } catch (error) {
                console.error('OAuth integration error:', error);

                if (error.message.includes('Popup was blocked')) {
                    tostAlert(intl.formatMessage(messages.pop_up_block), 'error');
                } else if (error.message.includes('OAuth timeout')) {
                    tostAlert(intl.formatMessage(messages.oauth_timeout), 'error');
                } else if (error.message.includes('OAuth popup was closed')) {
                    tostAlert(intl.formatMessage(messages.oauth_cancel), 'info');
                } else {
                    tostAlert(intl.formatMessage(messages.connection_error), 'error');
                }
                setModalData({ ...modalData, modalstatus: true });
            }
        } catch (error) {
            console.error('Integration error:', error);
            tostAlert(intl.formatMessage(messages.unexpected_error), 'error');
            setModalData({ ...modalData, modalstatus: true });
        } finally {
            setAuthLoading(false);
        }
    };

    const handleConnectionAuth = async (connector_type, user_id, user_mail, refresh_token) => {
        if (loading) return;
        setLoading(true);
        try {
            const userName = await getUserName();

            const collection_item = {
                name: connectionName,
                user_id: user_id,
                email: user_mail,
                refresh_token: refresh_token,
                status: "connected",
                auth_status: "valid",
                workspace_id: selectedWorkspace?.payload?.__auto_id__ ?? null,
                project_id: selectedProject?.payload?.__auto_id__ ?? null,
                created_on: moment().format('YYYY-MM-DD HH:mm:ss'),
                created_by: userName
            };

            switch (connector_type) {
                case 'google':
                    collection_item.source_type = 'google_drive';
                    break;
                case 'azure':
                    collection_item.source_type = 'one_drive';
                    break;
                case 'gmail':
                    collection_item.source_type = 'gmail';
                    collection_item.enabled = true;
                    collection_item.auth_required = true
                    break;
                case 'outlook':
                    collection_item.source_type = 'outlook';
                    collection_item.enabled = true;
                    collection_item.auth_required = true
                    break;
                default:
                    return;
            }

            const settingsObject = { collection_item };
            const create_response = await AnalyticsDataSourcesApi.addData(JSON.stringify(settingsObject));

            if (create_response.status === 200) {
                handleAnalyticsSteps('analytics_connector_datasource_status')
                tostAlert(intl.formatMessage(messages.connection_success), 'success');
                dispatch(setAnalyticsDataSourcesAdded(true))
                handleModalClose();
                navigate('/sources/analytics-tool/integrations')
                setSelectedMenuItem(1)
            } else {
                tostAlert(intl.formatMessage(messages.connection_error), 'error');
            }

        } catch (error) {
            console.error('Connection auth error:', error);
            tostAlert(intl.formatMessage(messages.connection_error), 'error');
        } finally {
            setLoading(false);
        }
    }

    const handleModalClose = () => {
        setModalData(modalDetails);
        setConnectionName('');
        setFormErrors({});
        setLoading(false);
        setAuthLoading(false);
    }

    const isRestrictedEnv = IS_SFS_INSTANCE || IS_PROD_INSTANCE;
    const isExternalService = IS_COMMUNITY_MODE || IS_AWS_MODE;
    let allowedIntegrations = null;

    if (isExternalService) {
        allowedIntegrations = ["mongodb", "ameya_extract"];
    } else if (isRestrictedEnv) {
        allowedIntegrations = ["mongodb", "ameya_extract", "google", "azure", "gmail", "outlook"];
    }

    const sortedConnectors = [...all_connectors].sort((a, b) => {
        const aSupported = !allowedIntegrations || allowedIntegrations.includes(a.value);
        const bSupported = !allowedIntegrations || allowedIntegrations.includes(b.value);
        return Number(bSupported) - Number(aSupported);
    });

    return (
        <div style={styles.mainContainer}>
            <Box sx={styles.cardContainer}>

                {(loading || authLoading) && <LoadBar />}

                <Typography sx={styles.mainHeadingText}>All Connectors</Typography>

                <Box marginTop={'30px'} paddingBottom={'30px'}>
                    <Grid container spacing={4}>
                        {sortedConnectors?.map((item, index) => {

                            if ((isRestrictedEnv || isExternalService) && item.value === "appflyte") {
                                return null;
                            }

                            const canAddIntegration = !allowedIntegrations || allowedIntegrations.includes(item.value);

                            return (
                                <Grid size={{ xs: 6, md: 4 }} key={index} >
                                    <Card sx={styles.card}>
                                        <CardContent sx={{ padding: 0, margin: 0 }}>
                                            <Box padding="20px 20px 0 20px">
                                                <Box sx={styles.cardHeader}>
                                                    <ReactSVG
                                                        src={IconSvg[item.icon]}
                                                        beforeInjection={(svg) => {
                                                            svg.setAttribute("style", "width: 24px; height:24px; display: block;");
                                                            svg.setAttribute("stroke", "#0B51C5");
                                                        }}
                                                    />
                                                    <Typography sx={{ ...styles.cardHeadingText, marginLeft: '10px' }}>{item?.label}</Typography>
                                                </Box>
                                                <Box sx={styles.cardBody}>
                                                    <Typography sx={styles.cardDescriptionText}>{item?.description}</Typography>
                                                </Box>

                                                {/* <Box sx={styles.cardFooter}>
                                                    <Typography sx={{ ...styles.cardDescriptionText, color: '#0B51C5', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                                        Learn more
                                                    </Typography>
                                                    <Box
                                                        sx={{ display: 'flex', alignItems: 'center' }}
                                                        onClick={() => handleConnectorSelection(item.value, item.label)}
                                                    >
                                                        <Typography sx={{ ...styles.cardDescriptionText, color: '#0B51C5', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                                            Add Integration
                                                        </Typography>
                                                        <IconButton>
                                                            <ArrowForward sx={{ color: '#0B51C5', height: '18px', width: '18px' }} />
                                                        </IconButton>
                                                    </Box>
                                                </Box> */}

                                                <Box sx={styles.cardFooter}>
                                                    <Box
                                                        sx={{ display: 'flex', alignItems: 'center' }}
                                                        onClick={canAddIntegration ? () => handleConnectorSelection(item.value, item.label) : undefined}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                ...styles.cardDescriptionText,
                                                                color: canAddIntegration ? '#0B51C5' : '#9E9E9E',
                                                                cursor: canAddIntegration ? 'pointer' : 'not-allowed',
                                                                '&:hover': canAddIntegration ? { textDecoration: 'underline' } : {},
                                                            }}
                                                        >
                                                            {canAddIntegration ? 'Add Integration' : 'Coming Soon'}
                                                        </Typography>

                                                        <IconButton disabled={!canAddIntegration}>
                                                            <ArrowForward
                                                                sx={{ color: canAddIntegration ? '#0B51C5' : '#9E9E9E', height: '18px', width: '18px' }}
                                                            />
                                                        </IconButton>
                                                    </Box>
                                                </Box>

                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )
                        })}
                    </Grid>
                </Box>


                <Modal open={modalData.modalstatus} onClose={handleModalClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                    <Box sx={mainStyles.modalcontainer}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {currentType && <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <ReactSVG
                                    src={currentType.icon}
                                    beforeInjection={(svg) => {
                                        svg.setAttribute("style", "width: 24px; height:24px; display: block;");
                                        svg.setAttribute("stroke", "#0B51C5");
                                    }}
                                />
                                <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>
                                    {currentType.label}
                                </Typography>
                            </Box>}
                            <IconButton onClick={handleModalClose}>
                                <Close />
                            </IconButton>
                        </Box>
                        <Box marginTop={'10px'}>
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', marginBottom: '5px' }}>Integration Name <span style={{ color: 'red' }}>*</span></Typography>
                            <TextField
                                size='small'
                                fullWidth
                                value={connectionName}
                                onChange={(e) => setConnectionName(e.target.value)}
                            />
                            {formErrors.connectionName && <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'red' }}>{formErrors.connectionName}</Typography>}
                        </Box>
                        <Box sx={{ marginTop: '25px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Button
                                onClick={() => handleConnectionIntegration()}
                                sx={styles.integrateBtn}
                                disabled={authLoading || loading}
                            >
                                <Typography sx={styles.integrateBtnText}>Integrate</Typography>
                            </Button>
                        </Box>
                    </Box>
                </Modal>

            </Box>
        </div >

    )
}

export default AnalyticsConnectors;